import { QuestInteractionHandler } from "@/app/games/(details)/QuestInteractionHandler";
import { WeeklyPointsBadge } from "@/app/games/(details)/quest-badges";
import { QuestTaskItem } from "@/app/games/(details)/quest-group";
import { useGameDetails } from "@/app/games/(details)/useGameDetails";
import { Card, CardContent } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Action, ControlType } from "@/context/AnalyticsProvider";
import { useQuestAnalytics } from "@/context/AnalyticsProvider/quests";
import { cn } from "@/lib/utils";
import { formatGenreLabel } from "@/utils/string";
import { ArrowRight } from "lucide-react";
import Image from "next/image";
import Link from "next/link";
import { useMemo } from "react";
import type { KeyedMutator } from "swr";
import type {
  CampaignQuest,
  CampaignQuestState,
  QuestCompletion,
  QuestCompletions,
} from "../QuestsV2/types";
import { List } from "../ui/list";
import { useQuestFilter } from "./QuestFilterProvider";

interface QuestCardProps {
  gameName: string;
  completedQuests?: QuestCompletion[];
  invalidateQuestCompletions: KeyedMutator<QuestCompletions>;
  gameIcon?: string;
  gameSlug?: string;
  gameGenres: string[];
  gameLandingPageUrl?: string;
  featured: boolean;
  gameId: string;
  quests: CampaignQuestState[];
  displayOrder: number;
  variant?: "default" | "compact";
}

export const QuestCard = ({
  gameName,
  completedQuests,
  invalidateQuestCompletions,
  gameIcon,
  gameSlug,
  gameId,
  gameGenres,
  gameLandingPageUrl,
  quests,
  displayOrder,
  variant = "default",
}: QuestCardProps) => {
  const { track } = useQuestAnalytics();
  const { filters, originalQuestsByGame } = useQuestFilter();
  const {
    onQuestClick,
    isWishlisted,
    completeQuest,
    allQuests,
    inProgressQuest,
    setInProgressQuest,
  } = useGameDetails(
    {
      quests,
      details: {
        game_id: gameId,
        name: gameName,
        landing_page_url: gameLandingPageUrl,
      },
    },
    completedQuests,
    invalidateQuestCompletions,
  );

  // Filter quests for display (hide completed if toggle is on)
  const displayedQuests = useMemo(() => {
    if (!filters.hideCompleted || !allQuests) return allQuests;

    // Use completedQuests as source of truth for determining completed quests
    const completedExternalIds = new Set(
      completedQuests?.map((q) => q.external_id) ?? [],
    );

    return allQuests.filter(
      (quest) => !completedExternalIds.has(quest.externalId),
    );
  }, [allQuests, filters.hideCompleted, completedQuests]);

  // Get the original (unfiltered) quests for this game to calculate progress
  // This ensures progress bar shows progress relative to ALL quests, not filtered ones
  const originalQuests = originalQuestsByGame.get(gameId) ?? [];

  // Build a map of completion counts by external_id for quick lookup
  const completionCountMap = useMemo(() => {
    const map = new Map<string, number>();
    for (const completion of completedQuests ?? []) {
      map.set(completion.external_id, completion.completion_count);
    }
    return map;
  }, [completedQuests]);

  // Calculate progress based on ORIGINAL quests (unaffected by filters) using weeklyPoints
  const totalWeeklyPointsEarned = useMemo(() => {
    return originalQuests.reduce((sum, quest) => {
      const completionCount = completionCountMap.get(quest.externalId) ?? 0;
      if (completionCount > 0) {
        return sum + (quest.weeklyPoints ?? 0) * completionCount;
      }
      return sum;
    }, 0);
  }, [originalQuests, completionCountMap]);

  const totalPossibleWeeklyPoints = useMemo(() => {
    return originalQuests.reduce(
      (sum, quest) => sum + (quest.weeklyPoints ?? 0),
      0,
    );
  }, [originalQuests]);

  // Ensure progress doesn't exceed 100% for recurring quests
  const progressPercentage =
    totalPossibleWeeklyPoints > 0
      ? Math.min(
          (totalWeeklyPointsEarned / totalPossibleWeeklyPoints) * 100,
          100,
        )
      : 0;

  const formattedGameGenres = useMemo(() => {
    return gameGenres?.map(formatGenreLabel).join(", ") || "";
  }, [gameGenres]);

  if (quests.length === 0) {
    return null;
  }

  const handleCompleteQuest = async (quest: CampaignQuest) => {
    const completedQuest = await completeQuest(quest);
    if (completedQuest) {
      track({
        action: Action.SUCCEEDED,
        extras: {
          id: "quest-finder",
          gameId: gameId,
          gameName: gameName,
          questId: quest.id.toString(),
          questType: quest.type,
          displayOrder: displayOrder,
        },
      });
    } else {
      track({
        action: Action.FAILED,
        extras: {
          id: "quest-finder",
          gameId,
          gameName,
          questId: quest.id.toString(),
          questType: quest.type,
          displayOrder: displayOrder,
        },
      });
    }
  };

  const handleQuestClick = (quest: CampaignQuestState) => {
    track({
      action: Action.PRESSED,
      extras: {
        id: "quest-finder",
        control: "quest",
        controlType: ControlType.LIST_ITEM,
        gameId: gameId,
        gameName: gameName,
        questId: quest.id.toString(),
        questType: quest.type,
        displayOrder: displayOrder,
      },
    });
    onQuestClick(quest);
  };

  const handleGameClick = () => {
    track({
      action: Action.PRESSED,
      extras: {
        id: "quest-finder",
        control: "ViewGamePage",
        controlType: ControlType.BUTTON,
        gameId: gameId,
        gameName: gameName,
        displayOrder: displayOrder,
      },
    });
  };
  return (
    <>
      <Card
        className={cn(
          "border-white/10 transition-all",
          gameSlug &&
            "cursor-pointer hover:-translate-y-0.5 hover:shadow-xl hover:bg-neutral-900/90 hover:border-white/20",
        )}
      >
        <CardContent>
          {/* Game Header */}
          <div className="flex gap-3 mb-2">
            {gameSlug && (
              <Link
                href={`/games/${gameSlug}`}
                className="text-white"
                onClick={handleGameClick}
              >
                <div
                  className={cn(
                    "size-12 rounded-lg flex items-center justify-center shrink-0 overflow-hidden",
                    gameIcon
                      ? "border-2 border-white/20 bg-neutral-800/70"
                      : "bg-transparent",
                  )}
                >
                  <Image
                    src={gameIcon || "/images/rewards/playgame.svg"}
                    alt={gameIcon ? gameName : "Play Game"}
                    width={gameIcon ? 44 : 48}
                    height={gameIcon ? 44 : 48}
                    className="object-contain"
                  />
                </div>
              </Link>
            )}
            <div className="flex-1 min-w-0">
              {gameSlug && (
                <Link
                  href={`/games/${gameSlug}`}
                  className="text-white"
                  onClick={handleGameClick}
                >
                  <h3 className="text-lg font-bold text-white">{gameName}</h3>
                </Link>
              )}
              {gameGenres && (
                <p className="text-xs text-white/60">{formattedGameGenres}</p>
              )}
            </div>
            {gameSlug && (
              <Link
                href={`/games/${gameSlug}`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-white"
                onClick={() => handleGameClick()}
              >
                <ArrowRight className="size-5" />
              </Link>
            )}
          </div>

          {/* Progress Bar with Weekly Points Badge */}
          <div className="flex items-center gap-2 mb-4">
            <Progress
              value={progressPercentage}
              className="h-1.5 bg-neutral-800/60 flex-1"
            />
            <WeeklyPointsBadge
              text={`${totalWeeklyPointsEarned} / ${Math.max(totalPossibleWeeklyPoints, totalWeeklyPointsEarned)}`}
              completed={totalWeeklyPointsEarned > 0}
            />
          </div>

          {/* Quest List */}
          <div
            className={cn(
              "border-t border-border pt-2 overflow-y-scroll",
              variant === "compact" ? "max-h-[180px]" : "max-h-[300px]",
            )}
          >
            <List variant="flush" size="md">
              {displayedQuests?.map((quest) => (
                <QuestTaskItem
                  key={quest.id}
                  quest={quest}
                  variant="compact"
                  disabled={!isWishlisted && quest.type !== "wishlist"}
                  onClick={() => handleQuestClick(quest)}
                />
              ))}
            </List>
          </div>
        </CardContent>
        <QuestInteractionHandler
          game={{
            details: {
              game_id: gameId,
              name: gameName,
              slug: gameSlug,
            },
            quests,
          }}
          completeQuest={handleCompleteQuest}
          inProgressQuest={inProgressQuest}
          setInProgressQuest={setInProgressQuest}
        />
      </Card>
    </>
  );
};
